@if (loading()) {
<app-page-loader loadingType="content" />
} @else {
<app-survey-header [survey]="survey"></app-survey-header>
<div class="p-3 h-[calc(100%-var(--spacing)*15)]">
  <app-breadcrumb [items]="[{ label: 'Surveys', route: '/surveys' },{ label: 'Survey Translations' }]"/>
  <div class="h-[calc(100%-var(--spacing)*9)] rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
    <div class="h-full w-full overflow-y-scroll">
      <div class="sticky flex top-0 left-0 bg-white dark:bg-gray-800 border-b border-stroke px-5 py-4 dark:border-strokedark sm:px-7.5">
        <div class="mb-2 flex-1">
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Languages: </label>
          <select
            [(ngModel)]="defaultLanguage"
            (change)="requestLanguageChange()"
            class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
          >
            @for(language of supportedLanguages; track language.lang; let i = $index){
            <option [value]="language.lang">
              {{ language.englishLabel }}
            </option>
            }
          </select>
        </div>
        @if (canEdit()) {
        <div class="flex items-center gap-2">
          @if (saveError()) {
          <span class="text-sm text-red-600">{{ saveError() }}</span>
          }
          @if(pendingChangesCount > 0){
          <button type="button" (click)="requestDiscard()"
              [disabled]="pendingChangesCount === 0 || saving()"
              class="rounded-lg border border-gray-400 bg-white px-3 py-1.5 text-md font-medium text-gray-600 hover:bg-gray-100
              disabled:opacity-50 disabled:bg-white disabled:cursor-not-allowed"
          >
            Discard
          </button>
          }
          <button type="button" (click)="requestSave()"
              [disabled]="pendingChangesCount === 0 || saving()"
              class="flex items-center gap-2 rounded-lg border border-blue-600 bg-blue-600 px-4 py-2 text-lg font-medium text-white hover:bg-white hover:text-blue-600
              disabled:opacity-50 disabled:bg-white disabled:border-gray-400 disabled:text-gray-600 disabled:hover:text-gray-600 disabled:cursor-not-allowed"
          >
            @if (saving()) {
              <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
              Saving...
            } @else {
              Save
              @if (pendingChangesCount > 0) {
              <span class="text-xs">({{ pendingChangesCount }} change{{ pendingChangesCount === 1 ? '' : 's' }})</span>
              }
            }
          </button>
        </div>
        }
      </div>

      <div class="p-3">
      @for(question of questionTranslation; track question.questionCode; let i = $index){
        <div class="mb-3 rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          <div class="p-2 text-sm font-bold text-gray-700 bg-blue-100 dark:bg-gray-700 dark:text-gray-200">{{question.questionCode}}</div>
          <div class="p-2">
            <div class="">
              <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Question title
              </label>
              <input [(ngModel)]="question.title" type="text" [disabled]="!canEdit()"
                class="h-11 w-full rounded-lg border bg-transparent px-4 text-sm dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                [class.border-orange-400]="isTranslationChanged(question.questionCode, null)"
                [class.border-gray-300]="!isTranslationChanged(question.questionCode, null)"
                [class.dark:border-gray-700]="!isTranslationChanged(question.questionCode, null)"/>
            </div>
          @if(question.choices.length){
            <div class="pl-5">
              <div class="p-2 text-md">Choices<hr></div>
            @for(c of question.choices; track c.choiceValue;){
              <div class="p-2">
                <label class="mb-1.5 mr-1.5 inline-block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Choice [{{c.choiceValue}}]: 
                </label>
                <input [(ngModel)]="c.choiceText" type="text" [disabled]="!canEdit()"
                  class="h-8 rounded-lg border bg-transparent px-4 text-sm dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                  [class.border-orange-400]="isTranslationChanged(question.questionCode, c.choiceValue)"
                  [class.border-gray-300]="!isTranslationChanged(question.questionCode, c.choiceValue)"
                  [class.dark:border-gray-700]="!isTranslationChanged(question.questionCode, c.choiceValue)"/>
              </div>
            }
            </div>
          }
          </div>
        </div>
      }
      </div>
    </div>
  </div>
</div>
}

@if (showSaveConfirm()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (click)="cancelSave()">
  <div class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900" (click)="$event.stopPropagation()">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Save changes?</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      {{ pendingChangesCount }} translation{{ pendingChangesCount === 1 ? '' : 's' }} will be saved for <span class="font-medium text-gray-700 dark:text-gray-300">{{ defaultLanguage }}</span>.
    </p>
    @if (saveError()) {
    <p class="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-400">{{ saveError() }}</p>
    }
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="cancelSave()" [disabled]="saving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Cancel
      </button>
      <button type="button" (click)="save()" [disabled]="saving()" class="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-70">
        @if (saving()) {
          <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
          Saving...
        } @else {
          Save
        }
      </button>
    </div>
  </div>
</div>
}

@if (showSaveResult()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (click)="closeSaveResult()">
  <div class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900" (click)="$event.stopPropagation()">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Save Result</h3>
    <div class="mt-3 rounded-lg px-3 py-2 text-sm" [class.bg-green-50]="showSaveResult()!.failed === 0" [class.bg-amber-50]="showSaveResult()!.failed > 0">
      @if (showSaveResult()!.success > 0) {
      <span class="font-medium text-green-700">{{ showSaveResult()!.success }} saved successfully.</span>
      }
      @if (showSaveResult()!.failed > 0) {
      <span class="ml-1 font-medium text-red-700">{{ showSaveResult()!.failed }} failed — items highlighted in orange.</span>
      }
    </div>
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="closeSaveResult()" class="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-70">
        Close
      </button>
    </div>
  </div>
</div>
}

@if (showLeaveConfirm()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
  <div class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Discard unsaved changes?</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      You have {{ pendingChangesCount }} unsaved change{{ pendingChangesCount === 1 ? '' : 's' }}. If you leave now they will be lost.
    </p>
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="cancelLeave()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Stay
      </button>
      <button type="button" (click)="confirmLeave()" class="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">
        Leave &amp; discard
      </button>
    </div>
  </div>
</div>
}

@if (showLanguageChangeConfirm()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (click)="cancelLanguageChange()">
  <div class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900" (click)="$event.stopPropagation()">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Switch language?</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      You have {{ pendingChangesCount }} unsaved change{{ pendingChangesCount === 1 ? '' : 's' }} for <span class="font-medium text-gray-700 dark:text-gray-300">{{ previousLanguage }}</span>. Switching language will discard them.
    </p>
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="cancelLanguageChange()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Cancel
      </button>
      <button type="button" (click)="confirmLanguageChange()" class="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">
        Switch &amp; discard
      </button>
    </div>
  </div>
</div>
}

@if (showDiscardConfirm()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (click)="cancelDiscard()">
  <div class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900" (click)="$event.stopPropagation()">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Discard changes?</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      {{ pendingChangesCount }} unsaved change{{ pendingChangesCount === 1 ? '' : 's' }} will be lost and translations will be restored to their last saved state.
    </p>
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="cancelDiscard()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Cancel
      </button>
      <button type="button" (click)="discard()" class="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">
        Discard
      </button>
    </div>
  </div>
</div>
}